import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faNoteSticky, faPlus, faMagnifyingGlass, faThumbtack, faStar,
  faBoxArchive, faTrash, faTag, faRobot, faLink, faRotateLeft,
  faXmark, faBolt, faCheckSquare, faMicrophone, faBook, faLightbulb,
  faEllipsisVertical, faTriangleExclamation, faStop,
} from '@fortawesome/free-solid-svg-icons';
import api from '../api/API';
import './NotesPage.css';

// ─── Constants ─────────────────────────────────────────────────────────────────

const TIPO_CONFIG = {
  quick:      { label: 'Rápida',    icon: faBolt,         color: '#6366f1' },
  rich:       { label: 'Rica',      icon: faBook,         color: '#10b981' },
  audio:      { label: 'Áudio',     icon: faMicrophone,   color: '#f59e0b' },
  reflection: { label: 'Reflexão',  icon: faLightbulb,    color: '#ec4899' },
};

const TIPO_TABS = [
  { id: 'all',        label: 'Todas' },
  { id: 'quick',      label: 'Rápidas' },
  { id: 'rich',       label: 'Ricas' },
  { id: 'reflection', label: 'Reflexões' },
  { id: 'favorita',   label: 'Favoritas' },
  { id: 'lixeira',    label: 'Lixeira' },
];

const EMPTY_FORM = {
  tipo: 'quick', titulo: '', conteudo: '', transcricao: '', tags: [],
  id_tarefa: '', id_meta_pdi: '', fixada: false, favorita: false,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'agora';
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}

function stripMarkdown(text) {
  if (!text) return '';
  return text.replace(/#{1,6}\s/g, '').replace(/[*_`~]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
}

// ─── NoteCard ─────────────────────────────────────────────────────────────────

function NoteCard({ note, onSelect, onTogglePin, onToggleFav, onDelete }) {
  const cfg = TIPO_CONFIG[note.tipo] || TIPO_CONFIG.quick;
  const preview = stripMarkdown(note.conteudo || note.transcricao || '');

  return (
    <div
      className={`note-card${note.fixada ? ' pinned' : ''}`}
      style={{ '--note-color': cfg.color }}
      onClick={() => onSelect(note)}
    >
      <div className="note-card-top">
        <span className="note-tipo-badge" style={{ color: cfg.color, background: `${cfg.color}18` }}>
          <FontAwesomeIcon icon={cfg.icon} /> {cfg.label}
        </span>
        <div className="note-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className={`note-icon-btn${note.fixada ? ' active' : ''}`}
            title="Fixar"
            onClick={() => onTogglePin(note.id)}
          >
            <FontAwesomeIcon icon={faThumbtack} />
          </button>
          <button
            className={`note-icon-btn${note.favorita ? ' active-gold' : ''}`}
            title="Favorita"
            onClick={() => onToggleFav(note.id)}
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
          <button className="note-icon-btn danger" title="Mover para lixeira" onClick={() => onDelete(note.id)}>
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>

      {note.titulo && <h4 className="note-card-title">{note.titulo}</h4>}

      {preview && (
        <p className="note-card-preview">
          {preview.slice(0, 160)}{preview.length > 160 ? '…' : ''}
        </p>
      )}

      {note.resumo && (
        <p className="note-card-resumo">
          <FontAwesomeIcon icon={faRobot} style={{ marginRight: 4, opacity: 0.5 }} />
          {note.resumo.split('\n')[0].replace('• ', '').slice(0, 100)}…
        </p>
      )}

      {note.tags?.length > 0 && (
        <div className="note-card-tags">
          {note.tags.slice(0, 4).map((t) => (
            <span key={t} className="note-tag-chip">#{t}</span>
          ))}
        </div>
      )}

      <div className="note-card-footer">
        <span className="note-card-time">{relativeTime(note.atualizado_em || note.criado_em)}</span>
        {(note.id_tarefa || note.id_meta_pdi) && (
          <span className="note-linked-badge">
            <FontAwesomeIcon icon={faLink} /> vinculada
          </span>
        )}
      </div>
    </div>
  );
}

// ─── AudioRecorder ────────────────────────────────────────────────────────────

function AudioRecorder({ onTranscribed }) {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob]   = useState(null);
  const [audioUrl, setAudioUrl]     = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [seconds, setSeconds]       = useState(0);
  const mrRef    = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const formatTime = (s) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      mrRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      alert('Permissão de microfone negada ou não disponível.');
    }
  };

  const stopRecording = () => {
    mrRef.current?.stop();
    setRecording(false);
    clearInterval(timerRef.current);
  };

  const handleTranscribe = async () => {
    if (!audioBlob) return;
    setTranscribing(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(audioBlob);
      });
      const res = await api.post('/notes/transcribe', { audio: base64, mimeType: audioBlob.type });
      if (res.data.text) onTranscribed(res.data.text);
    } catch (err) {
      console.error('[Notes] transcribe error', err);
      alert('Erro ao transcrever. Verifique a configuração da IA no servidor.');
    } finally {
      setTranscribing(false);
    }
  };

  const reset = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setSeconds(0);
  };

  return (
    <div className="audio-recorder">
      {!audioUrl ? (
        <button
          type="button"
          className={`audio-record-btn${recording ? ' recording' : ''}`}
          onClick={recording ? stopRecording : startRecording}
        >
          <FontAwesomeIcon icon={recording ? faStop : faMicrophone} />
          {recording ? `Parar  ${formatTime(seconds)}` : 'Gravar áudio'}
          {recording && <span className="audio-rec-dot" />}
        </button>
      ) : (
        <div className="audio-player-row">
          <audio src={audioUrl} controls className="audio-player" />
          <button type="button" className="audio-record-btn secondary" onClick={reset}>
            <FontAwesomeIcon icon={faMicrophone} /> Regravar
          </button>
          <button
            type="button"
            className="audio-transcribe-btn"
            disabled={transcribing}
            onClick={handleTranscribe}
          >
            <FontAwesomeIcon icon={faRobot} spin={transcribing} />
            {transcribing ? 'Transcrevendo...' : 'Transcrever com IA'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── NoteModal ────────────────────────────────────────────────────────────────

function NoteModal({ note, metas, tarefas, onClose, onSaved }) {
  const isEdit = !!note?.id;
  const [form, setForm] = useState(isEdit ? {
    tipo:        note.tipo || 'quick',
    titulo:      note.titulo || '',
    conteudo:    note.conteudo || '',
    transcricao: note.transcricao || '',
    tags:        note.tags || [],
    id_tarefa:   note.id_tarefa || '',
    id_meta_pdi: note.id_meta_pdi || '',
    fixada:      note.fixada || false,
    favorita:    note.favorita || false,
  } : { ...EMPTY_FORM });

  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState('');
  const [aiSummary, setAiSummary] = useState(note?.resumo || '');
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const textRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) set('tags', [...form.tags, t]);
    setTagInput('');
  };

  const removeTag = (t) => set('tags', form.tags.filter((x) => x !== t));

  const isAudio = form.tipo === 'audio';
  const canSave = isAudio
    ? !!(form.conteudo.trim() || form.transcricao.trim())
    : !!form.conteudo.trim();

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        titulo:      form.titulo.trim() || null,
        conteudo:    form.conteudo.trim() || null,
        transcricao: form.transcricao.trim() || null,
        id_tarefa:   form.id_tarefa ? Number(form.id_tarefa) : null,
        id_meta_pdi: form.id_meta_pdi ? Number(form.id_meta_pdi) : null,
      };
      const res = isEdit
        ? await api.put(`/notes/${note.id}`, payload)
        : await api.post('/notes', payload);
      onSaved(res.data.record);
    } catch (err) {
      console.error('[Notes] save error', err);
    } finally {
      setSaving(false);
    }
  };

  const handleAiTags = async () => {
    if (!note?.id || !form.conteudo) return;
    setAiLoading('tags');
    try {
      const res = await api.post(`/notes/${note.id}/ai/tags`);
      if (res.data.tags?.length) set('tags', res.data.tags);
    } catch { } finally { setAiLoading(''); }
  };

  const handleAiSummary = async () => {
    if (!note?.id) return;
    setAiLoading('summary');
    try {
      const res = await api.post(`/notes/${note.id}/ai/summary`);
      if (res.data.resumo) setAiSummary(res.data.resumo);
    } catch { } finally { setAiLoading(''); }
  };

  const handleAiSuggest = async () => {
    if (!note?.id) return;
    setAiLoading('suggest');
    try {
      const res = await api.post(`/notes/${note.id}/ai/suggest`);
      if (res.data.suggestion?.tipo) setAiSuggestion(res.data.suggestion);
    } catch { } finally { setAiLoading(''); }
  };

  const acceptSuggestion = () => {
    if (!aiSuggestion) return;
    if (aiSuggestion.tipo === 'meta')   set('id_meta_pdi', aiSuggestion.id);
    if (aiSuggestion.tipo === 'tarefa') set('id_tarefa',   aiSuggestion.id);
    setAiSuggestion(null);
  };

  return (
    <div className="note-modal-overlay" onClick={onClose}>
      <div className="note-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="note-modal-header">
          <div className="note-tipo-selector">
            {Object.entries(TIPO_CONFIG).map(([k, v]) => (
              <button
                key={k}
                className={`note-tipo-btn${form.tipo === k ? ' active' : ''}`}
                style={form.tipo === k ? { background: `${v.color}20`, color: v.color, borderColor: v.color } : {}}
                onClick={() => set('tipo', k)}
              >
                <FontAwesomeIcon icon={v.icon} /> {v.label}
              </button>
            ))}
          </div>
          <div className="note-modal-header-actions">
            <button
              className={`note-icon-btn${form.fixada ? ' active' : ''}`}
              title="Fixar"
              onClick={() => set('fixada', !form.fixada)}
            >
              <FontAwesomeIcon icon={faThumbtack} />
            </button>
            <button
              className={`note-icon-btn${form.favorita ? ' active-gold' : ''}`}
              title="Favorita"
              onClick={() => set('favorita', !form.favorita)}
            >
              <FontAwesomeIcon icon={faStar} />
            </button>
            <button className="note-icon-btn" onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>

        {/* Título */}
        <input
          className="note-modal-titulo"
          placeholder="Título (opcional)"
          value={form.titulo}
          onChange={(e) => set('titulo', e.target.value)}
        />

        {/* Conteúdo */}
        {form.tipo === 'audio' ? (
          <div className="audio-note-section">
            <AudioRecorder
              onTranscribed={(text) => {
                set('transcricao', text);
                set('conteudo', text);
              }}
            />
            <textarea
              ref={textRef}
              className="note-modal-conteudo"
              placeholder="Transcrição editável aparecerá aqui após gravar e transcrever..."
              value={form.conteudo}
              onChange={(e) => set('conteudo', e.target.value)}
              rows={7}
            />
          </div>
        ) : (
          <textarea
            ref={textRef}
            className="note-modal-conteudo"
            placeholder={
              form.tipo === 'quick'      ? 'Captura rápida...' :
              form.tipo === 'rich'       ? 'Markdown suportado: **negrito**, *itálico*, - lista...' :
                                           'O que aprendi hoje? O que faria diferente?'
            }
            value={form.conteudo}
            onChange={(e) => set('conteudo', e.target.value)}
            rows={10}
          />
        )}

        {/* AI Summary */}
        {aiSummary && (
          <div className="note-ai-summary">
            <FontAwesomeIcon icon={faRobot} style={{ marginRight: 6 }} />
            <span className="note-ai-label">Resumo IA</span>
            <pre className="note-summary-text">{aiSummary}</pre>
          </div>
        )}

        {/* AI Suggestion */}
        {aiSuggestion && (
          <div className="note-ai-suggestion">
            <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#f59e0b', marginRight: 6 }} />
            <span>IA sugere vincular a {aiSuggestion.tipo === 'meta' ? 'meta' : 'tarefa'} #{aiSuggestion.id}: <em>{aiSuggestion.motivo}</em></span>
            <button className="note-suggestion-accept" onClick={acceptSuggestion}>Aceitar</button>
            <button className="note-suggestion-dismiss" onClick={() => setAiSuggestion(null)}>Ignorar</button>
          </div>
        )}

        {/* Tags */}
        <div className="note-modal-tags-row">
          <FontAwesomeIcon icon={faTag} className="note-field-icon" />
          <div className="note-tags-wrap">
            {form.tags.map((t) => (
              <span key={t} className="note-tag-chip editable">
                #{t}
                <button onClick={() => removeTag(t)}><FontAwesomeIcon icon={faXmark} /></button>
              </span>
            ))}
            <input
              className="note-tag-input"
              placeholder="+ tag"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
            />
          </div>
          {isEdit && (
            <button
              className="note-ai-btn"
              title="Gerar tags com IA"
              disabled={!!aiLoading}
              onClick={handleAiTags}
            >
              <FontAwesomeIcon icon={faRobot} spin={aiLoading === 'tags'} /> Tags IA
            </button>
          )}
        </div>

        {/* Vínculos */}
        <div className="note-modal-links-row">
          <FontAwesomeIcon icon={faLink} className="note-field-icon" />
          <select
            className="note-modal-select"
            value={form.id_meta_pdi}
            onChange={(e) => set('id_meta_pdi', e.target.value)}
          >
            <option value="">— Meta PDI —</option>
            {metas.map((m) => <option key={m.id} value={m.id}>{m.titulo}</option>)}
          </select>
          <select
            className="note-modal-select"
            value={form.id_tarefa}
            onChange={(e) => set('id_tarefa', e.target.value)}
          >
            <option value="">— Tarefa —</option>
            {tarefas.map((t) => <option key={t.id} value={t.id}>{t.descricao?.slice(0, 60)}</option>)}
          </select>
        </div>

        {/* Footer */}
        <div className="note-modal-footer">
          <div className="note-modal-ai-group">
            {isEdit && (
              <>
                <button className="note-ai-btn" disabled={!!aiLoading} onClick={handleAiSummary}>
                  <FontAwesomeIcon icon={faRobot} spin={aiLoading === 'summary'} /> Resumir
                </button>
                <button className="note-ai-btn" disabled={!!aiLoading} onClick={handleAiSuggest}>
                  <FontAwesomeIcon icon={faRobot} spin={aiLoading === 'suggest'} /> Sugerir vínculo
                </button>
              </>
            )}
          </div>
          <div className="note-modal-save-group">
            <button className="note-btn-cancel" onClick={onClose}>Cancelar</button>
            <button
              className="note-btn-save"
              disabled={saving || !canSave}
              onClick={handleSave}
            >
              {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar nota'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const NotesPage = () => {
  const [notes, setNotes]       = useState([]);
  const [tags, setTags]         = useState([]);
  const [metas, setMetas]       = useState([]);
  const [tarefas, setTarefas]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [tab, setTab]           = useState('all');
  const [activeTag, setActiveTag] = useState('');
  const [busca, setBusca]       = useState('');
  const [selectedNote, setSelectedNote] = useState(null); // null = closed, {} = new, note = edit
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (tab === 'lixeira')  { params.lixeira = 'true'; }
      else if (tab === 'favorita') { params.favorita = 'true'; }
      else if (tab !== 'all') { params.tipo = tab; }
      if (activeTag) params.tag = activeTag;
      if (busca)     params.busca = busca;

      const [notesRes, tagsRes] = await Promise.allSettled([
        api.get('/notes', { params }),
        api.get('/notes/tags'),
      ]);

      if (notesRes.status === 'fulfilled') setNotes(notesRes.value?.data?.records || []);
      if (tagsRes.status === 'fulfilled')  setTags(tagsRes.value?.data?.records || []);
    } catch (err) {
      console.error('[Notes] load error', err);
    } finally {
      setLoading(false);
    }
  }, [tab, activeTag, busca]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const fetchContext = async () => {
      const [metasRes, tarefasRes] = await Promise.allSettled([
        api.get('/pdi/metas'),
        api.get('/tasks', { params: { concluido: false, limit: 50 } }),
      ]);
      if (metasRes.status === 'fulfilled')   setMetas(metasRes.value?.data?.records || []);
      if (tarefasRes.status === 'fulfilled') setTarefas(tarefasRes.value?.data?.records || []);
    };
    fetchContext();
  }, []);

  const openNew  = () => { setSelectedNote({}); setShowModal(true); };
  const openEdit = (note) => { setSelectedNote(note); setShowModal(true); };
  const closeModal = () => { setShowModal(false); setSelectedNote(null); };

  const handleSaved = (record) => {
    closeModal();
    load();
  };

  const handleTogglePin = async (id) => {
    await api.patch(`/notes/${id}/pin`).catch(() => {});
    load();
  };

  const handleToggleFav = async (id) => {
    await api.patch(`/notes/${id}/favorite`).catch(() => {});
    load();
  };

  const handleDelete = async (id) => {
    await api.delete(`/notes/${id}`).catch(() => {});
    load();
  };

  const handleRestore = async (id) => {
    await api.post(`/notes/${id}/restore`).catch(() => {});
    load();
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Deletar permanentemente esta nota?')) return;
    await api.delete(`/notes/${id}/permanent`).catch(() => {});
    load();
  };

  // Separate pinned from normal
  const pinned = notes.filter((n) => n.fixada);
  const normal = notes.filter((n) => !n.fixada);

  return (
    <div className="notes-page">
      {/* ── Header ── */}
      <div className="notes-header">
        <div className="notes-header-left">
          <h1>
            <FontAwesomeIcon icon={faNoteSticky} style={{ color: '#6366f1', marginRight: 10 }} />
            Notas
          </h1>
          <p>Capture ideias, reflexões e insights conectados ao seu desenvolvimento</p>
        </div>
        <button className="notes-create-btn" onClick={openNew}>
          <FontAwesomeIcon icon={faPlus} /> Nova nota
        </button>
      </div>

      {/* ── Search ── */}
      <div className="notes-search-bar">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="notes-search-icon" />
        <input
          className="notes-search-input"
          placeholder="Buscar por título, conteúdo ou transcrição..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        {busca && (
          <button className="notes-search-clear" onClick={() => setBusca('')}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="notes-tabs">
        {TIPO_TABS.map((t) => (
          <button
            key={t.id}
            className={`notes-tab${tab === t.id ? ' active' : ''}`}
            onClick={() => { setTab(t.id); setActiveTag(''); }}
          >
            {t.label}
            {t.id === 'lixeira' && <FontAwesomeIcon icon={faTrash} style={{ marginLeft: 5, opacity: 0.6 }} />}
          </button>
        ))}
      </div>

      {/* ── Tag cloud ── */}
      {tags.length > 0 && (
        <div className="notes-tag-cloud">
          <button
            className={`note-tag-filter${!activeTag ? ' active' : ''}`}
            onClick={() => setActiveTag('')}
          >
            Todas
          </button>
          {tags.slice(0, 20).map((t) => (
            <button
              key={t.tag}
              className={`note-tag-filter${activeTag === t.tag ? ' active' : ''}`}
              onClick={() => setActiveTag(activeTag === t.tag ? '' : t.tag)}
            >
              #{t.tag}
              <span className="note-tag-count">{t.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div className="notes-loading">
          <div className="notes-spinner" />
          <span>Carregando notas...</span>
        </div>
      ) : notes.length === 0 ? (
        <div className="notes-empty" onClick={openNew}>
          <FontAwesomeIcon icon={faNoteSticky} className="notes-empty-icon" />
          <p>{tab === 'lixeira' ? 'Lixeira vazia.' : 'Nenhuma nota encontrada.'}</p>
          {tab !== 'lixeira' && <span>Clique aqui ou em "Nova nota" para começar →</span>}
        </div>
      ) : (
        <>
          {/* Pinned */}
          {pinned.length > 0 && tab !== 'lixeira' && (
            <div className="notes-group">
              <div className="notes-group-label">
                <FontAwesomeIcon icon={faThumbtack} /> Fixadas
              </div>
              <div className="notes-grid">
                {pinned.map((n) => (
                  <NoteCard
                    key={n.id} note={n}
                    onSelect={openEdit}
                    onTogglePin={handleTogglePin}
                    onToggleFav={handleToggleFav}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Normal or lixeira */}
          {normal.length > 0 && (
            <div className="notes-group">
              {pinned.length > 0 && tab !== 'lixeira' && (
                <div className="notes-group-label">Outras notas</div>
              )}
              <div className="notes-grid">
                {(tab === 'lixeira' ? notes : normal).map((n) => (
                  tab === 'lixeira' ? (
                    <div key={n.id} className="note-card trash-card">
                      <div className="note-card-top">
                        <span className="note-tipo-badge" style={{ color: TIPO_CONFIG[n.tipo]?.color, background: `${TIPO_CONFIG[n.tipo]?.color}18` }}>
                          <FontAwesomeIcon icon={TIPO_CONFIG[n.tipo]?.icon} /> {TIPO_CONFIG[n.tipo]?.label}
                        </span>
                        <div className="note-card-actions">
                          <button className="note-icon-btn" title="Restaurar" onClick={() => handleRestore(n.id)}>
                            <FontAwesomeIcon icon={faRotateLeft} />
                          </button>
                          <button className="note-icon-btn danger" title="Deletar permanentemente" onClick={() => handlePermanentDelete(n.id)}>
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                      {n.titulo && <h4 className="note-card-title">{n.titulo}</h4>}
                      <p className="note-card-preview">{stripMarkdown(n.conteudo || '').slice(0, 120)}</p>
                      <div className="note-card-footer">
                        <span className="note-card-time">
                          Excluída {relativeTime(n.deletado_em)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <NoteCard
                      key={n.id} note={n}
                      onSelect={openEdit}
                      onTogglePin={handleTogglePin}
                      onToggleFav={handleToggleFav}
                      onDelete={handleDelete}
                    />
                  )
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Modal ── */}
      {showModal && (
        <NoteModal
          note={selectedNote}
          metas={metas}
          tarefas={tarefas}
          onClose={closeModal}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default NotesPage;
